export interface LocationState {
  country?: { id: string; name: string };
  city?: { id: string; name: string };
  district?: { id: string; name: string };
}

export interface Address {
  name: string;
  email: string;
  mobile: string;
  country: string;
  city: string;
  district: string;
  zipCode: string;
  latitude: string;
  longitude: string;
  address: string;
  nationalAddress: string;
  countryName?: string;
  cityName?: string;
  districtName?: string;
  streetName?: string;
  buildingName?: string;
  buildingNumber?: string;
  dialCode?: string;
  shortAddress?: string;
}

export interface ShipmentDetails {
  referenceNumber?: string;
  paymentType: string;
  numberOfPieces: string;
  totalWeight: string;
  serviceType?: string;
  shipmentType?: string;
  description?: string;
  shipmentCost?: string;
  codAmount?: string;
}

export interface OrderFormData {
  client?: string;
  clientId?: string;
  clientName?: string;
  sender: Address;
  receiver: Address;
  shipment: ShipmentDetails;
  products?: {
    pId: string;
    quantity: string;
    weight: number;
  }[];
}

export type AddressOption = "short" | "manual" | "map";

export interface ShortAddress {
  shortAddress: string;
  nationalAddress: {
    buildingNumber: string;
    street: string;
    secondaryNumber?: string;
    district: string;
    postalCode: string;
    city: string;
  };
  location?: {
    latitude: number;
    longitude: number;
    googleMapsLink?: string;
  };
  buildingNumber?: string;
  street?: string;
  secondaryNumber?: string;
  district?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  googleMapsLink?: string;
}

export interface PickupFormData {
  locationName: string;
  countryId: string;
  cityId: string;
  districtId: string;
  pinCode: string;
  fullAddress: string;
  latitude: string;
  longitude: string;
  shortAddress: string;
}

export interface PickupRequestFormData {
  shipmentsId: string[];
  requestType: string;
  vehicleType: string;
  locationId: string;
  pickupDate: string;
  timeSlot: string;
  specialInstructions: string;
}

export interface PickupDropOfRequest {
  shipmentsId: string[];
  pickupDate: string;
  timeSlot: string;
}

// Constants
export const SHIPMENT_SERVICE_OPTIONS = [
  {
    id: "EXPRESS_24_HR",
    value: "Express (24 Hr Delivery)",
    text: "Express (24 Hr Delivery)",
  },
  { id: "SAME_DAY", value: "Same Day Delivery", text: "Same Day Delivery" },
  { id: "NEXT_DAY", value: "Next Day Delivery", text: "Next Day Delivery" },
  { id: "ECONOMY", value: "Economy", text: "Economy" },
  { id: "INTERNATIONAL", value: "International", text: "International" },
];

export const ORDER_SHIPMENT_TYPE_OPTIONS = [
  { id: "PARCEL", value: "PARCEL", text: "PARCEL" },
];

export const PAYMENT_TYPE_OPTIONS = [
  { id: "CC", value: "CC", text: "CC" },
  { id: "COD", value: "COD", text: "COD" },
];

export interface Product {
  id: string;
  name: string;
  sku: string;
  category: string;
  categoryId?: string;
  subcategory: string;
  subCategory?: string;
  subCategoryId?: string;
  status: string;
  price: number;
  discount: number;
  minOrder: number;
  expiryDate?: string;
  image: string;
  mediaList?: any[];
  features: {
    hasVariants: boolean;
    hasExpiry: boolean;
    isDangerousGoods: boolean;
    hasTerms: boolean;
    hasCompliance: boolean;
  };
  storage: {
    type: string;
    capacity: number;
    trigger: number;
  };
  compliance: {
    commodity: string;
    hsCode: string;
    origin: string;
    destination: string;
    tradeType: string;
    duty: string;
    vat: string;
    restricted: string;
    license: string;
    documents: string;
  } | null;
  dimensions: {
    length: string;
    width: string;
    height: string;
    weight: string;
  };
  variants: Array<{
    sku: string;
    name: string;
    value: string;
    price: number;
    image?: any;
    storage?: any;
  }>;
  itemInfo?: Record<string, any>;
  shortDesc: string;
  longDesc: string;
  terms: string;
}

export interface Promo {
  id: string;
  merchant: string;
  sku: string;
  promoCode?: string;
  products: Array<{ productSku: string; qty: number; productId?: string }>;
  startDate: string;
  endDate: string;
  status?: string;
  toClient?: string;
  startEndDate?: string;
}

export interface Gift {
  id: string;
  merchant: string;
  clientId?: string;
  giftSku?: string;
  mainProductSku: string;
  mainQty: number;
  giftProductSku: string;
  giftQty: number;
  startDate: string;
  endDate: string;
  status?: string;
  merchantId?: string;
  toClient?: string;
  startEndDate?: string;
  giftItems?: Array<{ id: string; sku: string; qty: number }>;
}

export interface StorageItem {
  type: string;
  capacity: string;
  minimumQuantity: string;
}

export interface ProductFormState {
  sku: string;
  name: string;
  category: string;
  subcategory: string;
  price: string;
  discount: string;
  minOrder: string;
  expiryDate?: string;
  shortDesc: string;
  longDesc: string;
  storageType: string;
  storageCapacity: string;
  storageTrigger: string;
  storageList?: StorageItem[];
  mediaList?: any[];
  complianceCommodity: string;
  complianceHsCode: string;
  complianceOrigin: string;
  complianceDestination: string;
  complianceTradeType: string;
  complianceDuty: string;
  complianceVat: string;
  complianceRestricted: string;
  complianceLicense: string;
  complianceDocuments: string;
  dimensionsLength: string;
  dimensionsWidth: string;
  dimensionsHeight: string;
  dimensionsWeight: string;
  terms: string;
  features: {
    variants: boolean;
    expiry: boolean;
    dangerGoods: boolean;
    terms: boolean;
    compliance: boolean;
  };
  [key: string]: any;
}

export interface VariantForm {
  sku: string;
  name: string;
  value: string;
  price: string;
  image?: string | File;
  storageType?: string;
  storageCapacity?: string;
  storageTrigger?: string;
  storageList?: StorageItem[];
  storage?: StorageItem[];
}

